/**
 * G1 — payment-resume path predicate (checkout / donate only).
 *
 * Segment-aware: never treat `/account/**` or `/{tenant}/account/**` as payment
 * resume, even when the path contains a `donate` substring
 * (e.g. `/account/donate`, `/acme/account/donations`).
 */

/** Strip query + hash; collapse trailing slashes to a single path root. */
function normalizePaymentResumePath(pathWithQuery: string): string {
  const withoutHash = pathWithQuery.split('#', 1)[0] ?? pathWithQuery;
  const withoutQuery = withoutHash.split('?', 1)[0] ?? withoutHash;
  return withoutQuery.replace(/\/+$/, '') || '/';
}

function isAccountReturnPath(pathOnly: string): boolean {
  return (
    pathOnly === '/account' ||
    pathOnly.startsWith('/account/') ||
    /^\/[^/]+\/account(?:\/|$)/.test(pathOnly)
  );
}

function isCheckoutReturnPath(pathOnly: string): boolean {
  return (
    pathOnly === '/checkout' ||
    pathOnly.startsWith('/checkout/') ||
    /^\/[^/]+\/checkout(?:\/|$)/.test(pathOnly)
  );
}

function isDonateReturnPath(pathOnly: string): boolean {
  return (
    pathOnly === '/donate' ||
    pathOnly.startsWith('/donate/') ||
    /^\/[^/]+\/donate(?:\/|$)/.test(pathOnly)
  );
}

/**
 * True only for bare or tenant-scoped checkout / donate parks.
 * False for all `/account/**` and `/{tenant}/account/**` paths.
 */
export function isPaymentResumeReturnPath(pathWithQuery: string): boolean {
  if (typeof pathWithQuery !== 'string' || pathWithQuery.length === 0) {
    return false;
  }
  const pathOnly = normalizePaymentResumePath(pathWithQuery);
  if (isAccountReturnPath(pathOnly)) {
    return false;
  }
  return isCheckoutReturnPath(pathOnly) || isDonateReturnPath(pathOnly);
}

/**
 * Infer payment-resume source for park elevation.
 * `null` when {@link isPaymentResumeReturnPath} is false; otherwise
 * `'checkout'` if a checkout segment is present, else `'donate'`.
 */
export function inferPaymentResumeReturnSource(
  pathWithQuery: string,
): 'checkout' | 'donate' | null {
  if (!isPaymentResumeReturnPath(pathWithQuery)) {
    return null;
  }
  const pathOnly = normalizePaymentResumePath(pathWithQuery);
  if (isCheckoutReturnPath(pathOnly)) {
    return 'checkout';
  }
  return 'donate';
}
